import React, { useEffect, useRef } from "react"
import { Button, Spinner } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import moment from "moment"
import FeedCard from "./FeedCard"
import { useFeed } from "./useFeed"
import { FeedFilterValues } from "./types"

interface ActivityTimelineProps {
  filters: FeedFilterValues
  /** Called after the feed reloads from the top, so the page can refresh side panels. */
  onReloaded?: () => void
}

const dayLabel = (iso: string, t: (k: string) => string) => {
  const d = moment(iso)
  if (d.isSame(moment(), "day")) return t("Hoy")
  if (d.isSame(moment().subtract(1, "day"), "day")) return t("Ayer")
  return d.format(d.isSame(moment(), "year") ? "dddd D MMM" : "dddd D MMM YYYY")
}

const Skeleton = () => (
  <div className="feed-card feed-skeleton" aria-hidden="true">
    <div className="feed-avatar-col">
      <div className="feed-avatar sk" />
    </div>
    <div className="feed-main">
      <div className="sk sk-line w-50" />
      <div className="sk sk-line w-75" />
      <div className="sk sk-block" />
    </div>
  </div>
)

const ActivityTimeline = ({ filters, onReloaded }: ActivityTimelineProps) => {
  const { t } = useTranslation("global")
  const { items, hasMore, loading, error, newCount, capped, loadMore, reload, patchItems } = useFeed(filters)
  const sentinel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = sentinel.current
    if (!el || capped) return
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) loadMore()
    }, { rootMargin: "600px" })
    observer.observe(el)
    return () => observer.disconnect()
  }, [loadMore, capped])

  const showNew = async () => {
    window.scrollTo({ top: 0, behavior: "smooth" })
    await reload()
    onReloaded?.()
  }

  const onCommentPosted = (visitId: string) =>
    patchItems(
      i => (i.type === "visit" && i.id === `visit:${visitId}`) || (i.type === "visit_comment" && i.meta?.visitId === visitId),
      i => ({ ...i, commentCount: i.commentCount + 1 })
    )

  let lastDay = ""

  return (
    <div className="feed-timeline">
      {newCount > 0 && (
        <button type="button" className="feed-new-pill" onClick={showNew}>
          <i className="fas fa-arrow-up me-2" />
          {newCount === 1 ? t("1 nuevo") : `${newCount > 99 ? "99+" : newCount} ${t("nuevos")}`}
        </button>
      )}

      {items.map(item => {
        const day = moment(item.occurredAt).format("YYYY-MM-DD")
        const separator = day !== lastDay ? <div className="feed-day">{dayLabel(item.occurredAt, t)}</div> : null
        lastDay = day
        return (
          <React.Fragment key={item.id}>
            {separator}
            <FeedCard item={item} onCommentPosted={onCommentPosted} />
          </React.Fragment>
        )
      })}

      {loading && items.length === 0 && (
        <>
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </>
      )}

      {error && (
        <div className="feed-state">
          <i className="fas fa-triangle-exclamation fa-2x text-warning mb-2" />
          <div className="mb-2">{t("No se pudo cargar la actividad")}</div>
          <Button size="sm" onClick={() => (items.length ? loadMore() : reload())}>
            {t("Reintentar")}
          </Button>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="feed-state">
          <i className="fas fa-stream fa-2x text-muted mb-2" />
          <div>{t("No hay actividad para estos filtros")}</div>
        </div>
      )}

      {!error && hasMore && capped && !loading && (
        <div className="text-center my-3">
          <Button variant="outline-primary" size="sm" onClick={loadMore}>
            {t("Cargar más")}
          </Button>
        </div>
      )}

      {!loading && !error && !hasMore && items.length > 0 && (
        <div className="feed-end text-muted small">{t("No hay más actividad")}</div>
      )}

      <div ref={sentinel} style={{ height: 1 }} />
      {loading && items.length > 0 && (
        <div className="text-center my-2">
          <Spinner size="sm" animation="border" />
        </div>
      )}
    </div>
  )
}

export default ActivityTimeline
